import './rechnungen.css';
import { useEffect } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { t, tx } from '../../i18n';
import { fmtDate, fmtKB } from '../../lib/format';
import { eurC } from '../../lib/money';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import { Loading } from '../belege/Pages';
import { Sheet } from '../belege/Sheet';
import { useClientApp } from '../context';
import { useCloseSheet } from '../lohn/Pages';
import { usePage, type Crumb } from '../page';
import { rePath, STATE_BADGE } from './labels';
import type { InvoiceDetail, InvoiceItem, InvoicesResponse } from './types';
import { ShareFile } from '../../components/ShareFile';

// Bereich Rechnungen (wie in der Demo): Firma wählen → Rechnungen der Kanzlei an diese Firma, offene oben, mit der
// Summe der offenen. Eine Rechnung öffnet sich als Fenster (?rechnung=<id>) mit der PDF. Stornierte Rechnungen stehen mit
// ihrer Stornorechnung unter „Storniert“.
export function RechnungenRoutes() {
  return (
    <Routes>
      <Route index element={<FirmsPage />} />
      <Route path=":clientId" element={<Level />} />
      <Route path="*" element={<Navigate to={rePath()} replace />} />
    </Routes>
  );
}

function Level() {
  const { clients } = useClientApp();
  const clientId = useParams().clientId ?? '';
  if (!clients.some((c) => c.id === clientId)) return <Navigate to={rePath()} replace />;
  return (
    <>
      <FirmPage clientId={clientId} />
      <RechnungSheetHost />
    </>
  );
}

// Titel, Pfad und Zurück: Start › Rechnungen › <Firma>; mit nur einer Firma ohne Firmenwahl
function useRePage(clientName?: string) {
  const { clients } = useClientApp();
  const one = clients.length === 1;
  const crumbs: Crumb[] = [
    { label: t('Start'), to: '/app' },
    { label: t('Rechnungen'), to: clientName && !one ? rePath() : undefined },
  ];
  if (clientName) crumbs.push({ label: clientName });
  usePage({ title: t('Rechnungen'), crumbs, back: clientName && !one ? rePath() : '/app', nav: 'rechnungen' });
}

// Rechnungen · Für welche Firma? (nur mit mehreren Firmen)
function FirmsPage() {
  const { clients } = useClientApp();
  useRePage();
  if (clients.length === 1) return <Navigate to={rePath(clients[0]!.id)} replace />;
  return (
    <>
      <div className="hello">
        <h2>{t('Rechnungen')}</h2>
        <p>{t('Für welche Firma?')}</p>
      </div>
      <div className="list">
        {clients.map((c) => (
          <Link key={c.id} className="item" to={rePath(c.id)}>
            <span className="ico" aria-hidden="true">
              {c.name.charAt(0)}
            </span>
            <span className="tx">
              <b>{c.name}</b>
              <small>{t('Rechnungen Ihrer Kanzlei')}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}

function FirmPage({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const { clientName } = useClientApp();
  const res = useApi<InvoicesResponse>(`/api/app/invoices/${clientId}`, {
    live: ['invoices', 'notifications', 'clients'],
    clientId,
  });
  const d = res.data;
  const name = d?.client.name ?? clientName(clientId);
  useRePage(name);

  const open = d?.items.filter((x) => x.status === 'offen') ?? [];
  const paid = d?.items.filter((x) => x.status === 'bezahlt') ?? [];
  const cancelled = d?.items.filter((x) => x.status === 'storniert' || x.status === 'storno') ?? [];
  const withdrawn = d?.items.filter((x) => x.status === 'zurueckgezogen') ?? [];
  const show = (id: string) => navigate({ search: `?rechnung=${id}` }, { state: { sheet: true } });
  const section = (title: string, xs: InvoiceItem[]) =>
    xs.length > 0 && (
      <>
        <div className="sec">
          <h3>{title}</h3>
        </div>
        <div className="list">
          {xs.map((x) => (
            <InvoiceRow key={x.id} x={x} onOpen={() => show(x.id)} />
          ))}
        </div>
      </>
    );

  return (
    <>
      <div className="hello">
        <h2>{t('Rechnungen')}</h2>
        <p>
          {t('Rechnungen Ihrer Kanzlei an {name}', { name })}
          {name.endsWith('.') ? '' : '.'}
        </p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : !d.items.length ? (
        <div className="files">
          <div className="empty re-empty">
            {t('Noch keine Rechnungen von Ihrer Kanzlei. Sobald eine kommt, finden Sie sie hier, und die 🔔 Glocke meldet sie.')}
          </div>
        </div>
      ) : (
        <>
          <div className="card re-sum">
            <h3>{t('Offen')}</h3>
            <div className="re-big">{eurC(d.open)}</div>
            <div className="src">
              {open.length === 1
                ? t('1 Rechnung offen. Bitte überweisen Sie mit der Rechnungsnummer als Verwendungszweck.')
                : open.length
                  ? t('{n} Rechnungen offen. Bitte überweisen Sie mit der Rechnungsnummer als Verwendungszweck.', {
                      n: open.length,
                    })
                  : paid.length
                    ? t('Alles bezahlt, danke!')
                    : t('Nichts offen.')}
            </div>
          </div>
          {section(t('Offen'), open)}
          {section(t('Bezahlt'), paid)}
          {section(t('Storniert'), cancelled)}
          {section(t('Zurückgezogen'), withdrawn)}
        </>
      )}
    </>
  );
}

function InvoiceRow({ x, onOpen }: { x: InvoiceItem; onOpen(): void }) {
  const badge = STATE_BADGE[x.state];
  return (
    <button type="button" className="item" onClick={onOpen}>
      <span className="ico" aria-hidden="true">
        €
      </span>
      <span className="tx">
        <b>{tx(x.service)}</b>
        <small>
          {x.number} ·{' '}
          {x.status === 'bezahlt'
            ? t('bezahlt am {date}', { date: fmtDate(x.paidOn ?? x.dueDate) })
            : x.status === 'zurueckgezogen'
              ? t('von Ihrer Kanzlei zurückgezogen')
              : x.status === 'storniert'
                ? x.storno
                  ? t('storniert mit {number}', { number: x.storno.number })
                  : t('storniert')
                : x.status === 'storno'
                  ? t('vom {date}', { date: fmtDate(x.invoiceDate) })
                  : t('fällig am {date}', { date: fmtDate(x.dueDate) })}
        </small>
        <span className="iamt">
          <b>{eurC(x.amount)}</b>
          <span className={badge.cls}>{t(badge.label)}</span>
          {x.new && <span className="badge b-warn">{t('Neu')}</span>}
        </span>
      </span>
      <span className="chev" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

// Eine Rechnung (?rechnung=<id>): PDF, Angaben, Herunterladen und was zu tun ist
function RechnungSheetHost() {
  const [params] = useSearchParams();
  const close = useCloseSheet('rechnung');
  const id = params.get('rechnung');
  return id ? <RechnungSheet key={id} id={id} onClose={close} /> : null;
}

function RechnungSheet({ id, onClose }: { id: string; onClose(): void }) {
  const { config } = useClientApp();
  const res = useApi<InvoiceDetail>(`/api/app/invoice/${id}`, { live: ['invoices'] });
  const x = res.data;

  // Meldungen dazu gelten als gelesen, sobald das Fenster die Rechnung zeigt
  const loaded = !!x;
  useEffect(() => {
    if (loaded) api.post(`/api/app/invoice/${id}/read`).catch(() => {});
  }, [id, loaded]);

  if (!x) {
    return (
      <Sheet title={t('Rechnung')} onClose={onClose}>
        {res.error ? <p className="meta">{res.error}</p> : <div className="spinner" aria-label={t('Lädt')} />}
      </Sheet>
    );
  }

  const url = `/api/app/invoice/${x.id}/file`;
  const state =
    x.status === 'bezahlt' ? t('bezahlt am {date}', { date: fmtDate(x.paidOn ?? x.dueDate) }) : t(STATE_BADGE[x.state].label);
  const foot =
    x.status === 'bezahlt'
      ? t('Bezahlt am {date}, danke.', { date: fmtDate(x.paidOn ?? x.dueDate) })
      : x.status === 'zurueckgezogen'
        ? t('Ihre Kanzlei hat diese Rechnung zurückgezogen. Sie müssen sie nicht bezahlen.')
        : x.status === 'storniert'
          ? t('Ihre Kanzlei hat diese Rechnung storniert. Sie müssen sie nicht bezahlen.')
          : x.status === 'storno'
            ? t('Mit dieser Stornorechnung hebt Ihre Kanzlei die Rechnung {number} auf. Sie müssen nichts bezahlen.', {
                number: x.stornoOf ?? '',
              })
            : t('Bitte überweisen Sie {amount} bis {date} mit dem Verwendungszweck {number}.', {
                amount: eurC(x.amount),
                date: fmtDate(x.dueDate),
                number: x.number,
              });
  return (
    <Sheet
      title={
        x.status === 'storno' ? t('Stornorechnung {number}', { number: x.number }) : t('Rechnung {number}', { number: x.number })
      }
      onClose={onClose}
    >
      <div className="meta">
        {config.kanzleiName} · {t('an {name} · vom {date}', { name: x.clientName, date: fmtDate(x.invoiceDate) })}
      </div>
      <Preview url={url} mime={x.file.mime} name={x.file.name} />
      <dl className="akte re-tbl">
        <div>
          <dt>{t('Leistung')}</dt>
          <dd>{tx(x.service)}</dd>
        </div>
        {x.status !== 'storno' && (
          <div>
            <dt>{t('Fällig am')}</dt>
            <dd>{fmtDate(x.dueDate)}</dd>
          </div>
        )}
        <div>
          <dt>{t('Status')}</dt>
          <dd>{state}</dd>
        </div>
        <div>
          <dt>{t('Betrag')}</dt>
          <dd>
            <b>{eurC(x.amount)}</b>
          </dd>
        </div>
      </dl>
      <p className="re-foot">{foot}</p>
      <div className="row">
        <a className="btn2 dark" href={`${url}?download=1`} download>
          {t('PDF herunterladen')}
        </a>
        <ShareFile url={`${url}?download=1`} name={x.file.name} />
      </div>
      <p className="src">
        {x.file.name} · {fmtKB(x.file.size)}
      </p>
    </Sheet>
  );
}
